import React, { useState, useEffect } from 'react';
import { Card, Spin, Typography, Empty } from 'antd';
import { DualAxes } from '@ant-design/charts';
import { useTranslation } from 'react-i18next';
import { fetchKeepaHistory } from '../api/client';

// Map i18n language → Intl date locale tag for short month/day labels.
const DATE_LOCALES: Record<string, string> = {
  ru: 'ru-RU',
  uk: 'uk-UA',
  en: 'en-US',
};

interface BsrChartProps {
  asin: string;
}

interface ChartRow {
  date: string;
  ts: number;
  bsr: number | null;
  price: number | null;
}

const BsrChart: React.FC<BsrChartProps> = ({ asin }) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState<ChartRow[]>([]);
  const [error, setError] = useState('');
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (!asin) return;
    let cancelled = false;
    setLoading(true);
    setError('');

    // Match Intl locale to active i18n language so axis labels are localized.
    const baseLang = (i18n.language || 'en').toLowerCase().split('-')[0];
    const dateLocale = DATE_LOCALES[baseLang] || 'en-US';

    fetchKeepaHistory(asin, 365)
      .then((res) => {
        if (cancelled) return;

        // Collect all timestamps and build maps
        const allTimestamps = new Set<number>();
        const bsrMap = new Map<number, number>();
        const priceMap = new Map<number, number>();

        for (const p of res.bsr) {
          const day = Math.floor(p.x / 86400000) * 86400000;
          allTimestamps.add(day);
          bsrMap.set(day, p.y);
        }

        for (const p of res.price) {
          const day = Math.floor(p.x / 86400000) * 86400000;
          allTimestamps.add(day);
          priceMap.set(day, p.y);
        }

        // Sort timestamps and build rows with forward-fill for price
        const sortedTs = Array.from(allTimestamps).sort((a, b) => a - b);
        let lastPrice: number | null = null;

        const rows: ChartRow[] = sortedTs.map((ts) => {
          const price = priceMap.get(ts);
          if (price !== undefined) {
            lastPrice = price;
          }
          return {
            ts,
            date: new Date(ts).toLocaleDateString(dateLocale, { month: 'short', day: 'numeric' }),
            bsr: bsrMap.get(ts) ?? null,
            price: lastPrice,
          };
        });

        setChartData(rows);
      })
      .catch(() => {
        if (!cancelled) setError(t('idea_calc.bsr_chart.load_error'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [asin, i18n.language, t]);

  const config = {
    xField: 'date',
    data: chartData,
    height: 280,
    children: [
      {
        type: 'line' as const,
        yField: 'bsr',
        shapeField: 'smooth',
        scale: {
          y: { reverse: true },
        },
        axis: {
          y: { title: t('idea_calc.bsr_chart.axis_bsr'), titleFill: '#52c41a' },
        },
        style: { lineWidth: 1.5, stroke: '#52c41a' },
      },
      {
        type: 'line' as const,
        yField: 'price',
        shapeField: 'hv',
        scale: {
          y: { independent: true },
        },
        axis: {
          y: { position: 'right' as const, title: t('idea_calc.bsr_chart.axis_price'), titleFill: '#eb2f96', grid: null },
        },
        style: { lineWidth: 2, stroke: '#eb2f96' },
      },
    ],
    axis: {
      x: { label: { autoRotate: true } },
    },
    interaction: {
      tooltip: {
        shared: true,
        crosshairs: true,
        render: (_: unknown, { title, items }: { title: string; items: Array<{ name: string; value: number; color: string }> }) => {
          const bsrItem = items.find((i) => i.name === 'bsr');
          const priceItem = items.find((i) => i.name === 'price');
          return `
            <div style="padding: 8px 12px; font-size: 12px;">
              <div style="margin-bottom: 4px; font-weight: 500;">${title}</div>
              ${bsrItem ? `<div style="color: #52c41a;">BSR: ${bsrItem.value?.toLocaleString() ?? '—'}</div>` : ''}
              ${priceItem ? `<div style="color: #eb2f96;">Buy Box: $${priceItem.value?.toFixed(2) ?? '—'}</div>` : ''}
            </div>
          `;
        },
      },
    },
  };

  return (
    <Card
      title={`${t('idea_calc.bsr_chart.title_prefix')} — ${asin}`}
      variant="outlined"
      size="small"
      extra={
        <Typography.Link onClick={() => setCollapsed(!collapsed)}>
          {collapsed ? t('idea_calc.bsr_chart.expand') : t('idea_calc.bsr_chart.collapse')}
        </Typography.Link>
      }
    >
      {!collapsed && (
        <Spin spinning={loading}>
          {error ? (
            <Typography.Text type="danger">{error}</Typography.Text>
          ) : chartData.length === 0 && !loading ? (
            <Empty description={t('idea_calc.bsr_chart.no_data')} />
          ) : (
            <DualAxes {...config} />
          )}
        </Spin>
      )}
    </Card>
  );
};

export default BsrChart;
